import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { captureException } from '@sentry/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CopyIcon } from '../../assets/icons';
import { useHasClipboardSupport } from '../../hooks/useHasClipboardSupport';
import { useModalState } from '../../hooks/useModalState';
import { usePositionById } from '../../hooks/usePositions';
import { useProductPricesSub } from '../../hooks/useProductPricesSub';
import { useProducts } from '../../hooks/useProducts';
import { formatPercentage } from '../../lib/formatters';
import { copyImageToClipboard, downloadItem, generateImageFromHtml } from '../../lib/imageDownload';
import { divide, getDecimalPlaces } from '../../lib/math';
import { calcPnl, calcPnlPercentage } from '../../lib/positionHelpers';
import { showErrorToast, showInfoToast } from '../../lib/showToast';
import { shareToX } from '../../lib/socialSharing';
import { Button } from '../catalyst-ui-kit/button';
import { Checkbox } from '../catalyst-ui-kit/checkbox';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { Tooltip } from '../Tooltip/Tooltip';
import { PnlCardDisplay } from './PnlCardDisplay';
import { PnlCardModalErrorContent } from './PnlCardModalErrorContent';
import { PositionWithData } from './types';

const BACKGROUNDS_RED = [
  '/images/pnl/pnl-red-1.png',
  '/images/pnl/pnl-red-2.png',
  '/images/pnl/pnl-red-3.png',
  // '/images/pnl/pnl-red-4.png',
  '/images/pnl/pnl-red-5.png',
  // '/images/pnl/pnl-red-6.png',
];
const BACKGROUNDS_GREEN = [
  '/images/pnl/pnl-green-1.png',
  '/images/pnl/pnl-green-2.png',
  '/images/pnl/pnl-green-3.png',
  // '/images/pnl/pnl-green-4.png',
  '/images/pnl/pnl-green-5.png',
  // '/images/pnl/pnl-green-6.png',
];

// Assuming we keep the same number of images for red/green backgrounds
const getRandomIndex = () => Math.floor(Math.random() * BACKGROUNDS_GREEN.length);

const EXPORT_CARD_WIDTH = 630;
const EXPORT_CARD_HEIGHT = 371;

const getExportFilename = (position?: PositionWithData) => {
  const isLong = (position?.side ?? 0) === 0;
  const ticker = position?.displayTicker || 'position';
  const side = isLong ? 'LONG' : 'SHORT';
  const pnlStr = position?.pnlPercentage
    ? formatPercentage(position.pnlPercentage, { signDisplay: 'exceptZero' })
        .replace('%', '')
        .replace(/[^a-zA-Z0-9]/g, '')
    : '';
  return `${ticker}-${side}-${pnlStr}-PNL.png`;
};

type PnlCardModalUiProps = {
  open: boolean;
  hidePnl: boolean;
  isError: boolean;
  isLoading: boolean;
  onClose: () => void;
  onTogglePnl: () => void;
  position?: PositionWithData;
  hasClipboardSupport: boolean;
};

export function PnlCardModalUi({
  open,
  hidePnl,
  onClose,
  isError,
  position,
  isLoading,
  onTogglePnl,
  hasClipboardSupport,
}: PnlCardModalUiProps) {
  const [activeIndex, setActiveIndex] = useState(getRandomIndex());

  // CTA states
  const [isSaving, setIsSaving] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const exportCardRef = useRef<HTMLDivElement>(null);

  const backgrounds = (position?.pnlPercentage ?? 0n) >= 0n ? BACKGROUNDS_GREEN : BACKGROUNDS_RED;

  const handleSaveCard = async () => {
    if (!exportCardRef.current || !position) return;

    setIsSaving(true);
    try {
      const dataUrl = await generateImageFromHtml(
        exportCardRef.current,
        EXPORT_CARD_WIDTH,
        EXPORT_CARD_HEIGHT
      );
      downloadItem(dataUrl, getExportFilename(position));
    } catch (error) {
      console.error('Error generating image:', error);
      captureException(error);
      showErrorToast('Unable to save image');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!exportCardRef.current || !position) return;

    setIsCopying(true);
    try {
      const dataUrl = await generateImageFromHtml(
        exportCardRef.current,
        EXPORT_CARD_WIDTH,
        EXPORT_CARD_HEIGHT
      );
      await copyImageToClipboard(dataUrl);
      showInfoToast('P&L card copied to clipboard');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      captureException(error);
      showErrorToast('Unable to copy image. Try downloading it instead');
    } finally {
      setIsCopying(false);
    }
  };

  const handleShareToX = async () => {
    if (!exportCardRef.current || !position) return;

    setIsSharing(true);
    try {
      if (hasClipboardSupport) {
        // Copy to clipboard before creating post
        const dataUrl = await generateImageFromHtml(
          exportCardRef.current,
          EXPORT_CARD_WIDTH,
          EXPORT_CARD_HEIGHT
        );
        await copyImageToClipboard(dataUrl);
      }

      const post = `Trade ${position.baseTokenName} with me on @etherealdex at ${window.location.origin}`;
      shareToX(post);
    } catch (error) {
      console.error('Error preparing image for X:', error);
      captureException(error);
      showErrorToast(
        'There was an error copying the image. Please try the Copy button first, then share.'
      );
    } finally {
      setIsSharing(false);
    }
  };

  // Reset active index when modal closes
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    if (!open) {
      timeout = setTimeout(() => {
        setActiveIndex(getRandomIndex());
      }, 500);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [open, setActiveIndex]);

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title="Share P&L"
      panelClassName="max-w-[1042px]"
    >
      {isError ? (
        <PnlCardModalErrorContent onClose={onClose} />
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Visible preview card - responsive for UI */}
          <div className="basis-2/3 overflow-hidden rounded lg:aspect-video">
            <PnlCardDisplay
              hidePnl={hidePnl}
              position={position}
              isLoading={isLoading}
              backgroundUrl={backgrounds[activeIndex]!}
            />
          </div>
          {/* Hidden export card with fixed dimensions - used only for image generation */}
          {open && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                top: '-9999px',
                overflow: 'hidden',
                width: `${EXPORT_CARD_WIDTH}px`,
                height: `${EXPORT_CARD_HEIGHT}px`,
              }}
            >
              <div
                style={{
                  width: `${EXPORT_CARD_WIDTH}px`,
                  height: `${EXPORT_CARD_HEIGHT}px`,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
              >
                <PnlCardDisplay
                  // Use fixed styles for export
                  forExport
                  hidePnl={hidePnl}
                  position={position}
                  isLoading={isLoading}
                  cardRef={exportCardRef}
                  backgroundUrl={backgrounds[activeIndex]!}
                />
              </div>
            </div>
          )}
          <div className="flex basis-1/3 flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row lg:flex-col">
              <div className="flex flex-1 flex-col gap-2">
                <p className="text-xs text-contentSecondary">Display</p>
                <div className="flex items-center justify-between gap-x-3 md:justify-start lg:justify-between">
                  <label
                    onClick={onTogglePnl}
                    htmlFor="showPnlCheckbox"
                    className="cursor-pointer text-sm font-medium"
                  >
                    P&L in $
                  </label>
                  <Checkbox
                    id="showPnlCheckbox"
                    checked={!hidePnl}
                    onChange={onTogglePnl}
                    aria-label="Hide P&L value"
                  />
                </div>
              </div>
              <div className="relative mt-2 grid flex-1 grid-cols-2 gap-2">
                {backgrounds.map((src, idx) => (
                  <div
                    key={idx}
                    role="button"
                    onClick={() => setActiveIndex(idx)}
                    className="relative aspect-video cursor-pointer"
                  >
                    <div className="size-full overflow-hidden rounded-md border border-borderPrimary">
                      <img
                        src={src}
                        alt={`Background ${idx + 1}`}
                        className="size-full object-cover"
                        draggable={false}
                      />
                    </div>
                    <AnimatePresence mode="wait">
                      {activeIndex === idx && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="pointer-events-none absolute -inset-1 z-10 origin-center rounded-lg border-2 border-contentBlue"
                        />
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-auto grid grid-cols-2 gap-2 pt-4 md:grid-cols-4 lg:grid-cols-2">
              {hasClipboardSupport && (
                <div className="col-span-2 md:col-span-2">
                  <Button
                    outline
                    onClick={handleCopyToClipboard}
                    className="w-full text-contentPrimary"
                    disabled={isSaving || isCopying || isSharing || isLoading}
                  >
                    <CopyIcon className="w-5 stroke-contentPrimary" />
                    {isCopying ? 'Copying...' : 'Copy Image'}
                  </Button>
                </div>
              )}
              <div className="col-span-1 md:col-span-1">
                <Button
                  outline
                  className="w-full"
                  onClick={handleSaveCard}
                  disabled={isSaving || isCopying || isSharing || isLoading}
                >
                  <ArrowDownTrayIcon className="stroke-contentPrimary" width={20} />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>

              <div className="col-span-1 md:col-span-1">
                <Tooltip
                  placement="bottom"
                  className="w-full"
                  content={
                    hasClipboardSupport
                      ? 'Image will also be copied to clipboard'
                      : 'Save the image before you post'
                  }
                >
                  <Button
                    outline
                    className="w-full text-contentPrimary"
                    onClick={handleShareToX}
                    disabled={isSaving || isCopying || isSharing || isLoading}
                  >
                    {isSharing ? 'Sharing...' : 'Share on'}
                    {!isSharing && (
                      <img src="/community-icons/X.svg" alt="x.com" className="-ml-2 w-5" />
                    )}
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
}

export function PnlCardModal() {
  const hasClipboardSupport = useHasClipboardSupport();

  const setPnlCardModal = useModalState((x) => x.setPnlCardModal);
  const { openOrClosed, positionId } = useModalState((x) => x.pnlCardModal);

  const [hidePnl, setHidePnl] = useState(true);

  // Prevent content flicker when closing modal
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    if (openOrClosed === 'closed' && positionId !== undefined) {
      timeout = setTimeout(() => {
        setPnlCardModal({ openOrClosed: 'closed', positionId: undefined });
      }, 300);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [openOrClosed, positionId, setPnlCardModal]);

  const {
    data: position,
    isLoading: positionLoading,
    isError: positionError,
  } = usePositionById(positionId);
  const { data: products = [], isLoading: productsLoading, isError: productsError } = useProducts();
  const { data: pricesByProductId, isPending: pricesLoading } = useProductPricesSub();

  const product = useMemo(() => {
    if (position) {
      return products.find((p) => p.id === position.productId);
    }

    return null;
  }, [position, products]);

  const price = useMemo(() => {
    if (position) {
      return pricesByProductId[position.productId];
    }

    return undefined;
  }, [position, pricesByProductId]);

  const positionWithData: PositionWithData | undefined = useMemo(() => {
    if (!position || !price || !product) {
      return undefined;
    }

    const sharedData = {
      ticker: product.ticker,
      baseTokenName: product.baseTokenName,
      displayTicker: product.displayTicker,
      priceDecimals: getDecimalPlaces(product.tickSize),
    };

    if (position.size === 0n) {
      return {
        ...position,
        ...sharedData,
        pnl: position.realizedPnl,
        pnlPercentage: divide(position.realizedPnl, position.totalIncreaseNotional),
        exitOrOraclePrice: divide(position.totalDecreaseNotional, position.totalDecreaseQuantity),
        entryPrice: divide(position.totalIncreaseNotional, position.totalIncreaseQuantity),
      };
    } else {
      return {
        ...position,
        ...sharedData,
        entryPrice: position.avgPrice,
        exitOrOraclePrice: price.oraclePrice,
        pnl: price?.oraclePrice
          ? calcPnl(price.oraclePrice, position.avgPrice, position.size)
          : undefined,
        pnlPercentage: price?.oraclePrice
          ? calcPnlPercentage(price.oraclePrice, position.avgPrice, position.size)
          : undefined,
      };
    }
  }, [position, price, product]);

  return (
    <PnlCardModalUi
      hidePnl={hidePnl}
      onTogglePnl={() => {
        setHidePnl((x) => !x);
      }}
      position={positionWithData}
      open={openOrClosed === 'open'}
      isError={positionError || productsError}
      hasClipboardSupport={hasClipboardSupport}
      isLoading={positionLoading || productsLoading || pricesLoading}
      onClose={() => {
        setPnlCardModal({ openOrClosed: 'closed', positionId });
      }}
    />
  );
}
